import type Immutable from 'immutable';

import type { DashboardVideo, Project, User } from 'js/src/client';
import type { View } from 'js/src/data/dashboard/dashboard-types';
import { DEFAULT_VIDEOS_SORT_OPTION, VIDEOS_KEY } from 'js/src/data/dashboard/dashboard-types';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { DeepReadonly, ImmutableMap } from 'js/src/libs/consts';

/**
 * Returns a modified project that has properties necessary for video storage
 * Used when setting all projects for the first time or creating a new project
 * Else, use existing dashboard actions
 */
export function convertProjectToView(project: DeepReadonly<Project>): View {
    const videos = PlainJsStoreORM.createEmpty<DeepReadonly<DashboardVideo>, number>().data;
    return {
        id: project.id,
        name: project.name,
        subTitle: undefined,
        icon: project.icon,
        color: project.color,
        moreVideosToLoad: true,
        loadedUpToPage: 0,
        videosSortBy: DEFAULT_VIDEOS_SORT_OPTION,
        workspaceId: project.workspace,
        [VIDEOS_KEY]: videos,
    };
}

/**
 * - Parses an Immutable list of members from Redux to a format for react-select
 * - Omits the current user from the result
 */
export function parseMembers(
    memberList: Immutable.List<ImmutableMap<User>>,
    excludeCurrentUser: boolean = true,
): {
    label: string;
    value: number;
    valueLabel: string;
    primaryLabel: string;
}[] {
    if (!memberList || !memberList.size) {
        return [];
    }
    if (excludeCurrentUser) {
        memberList = memberList.filter((member) => member.get('id') !== SERVER.USER.id);
    }
    return memberList
        .map((member) => {
            const { id, fullName, email, role } = getMemberDetails(member);
            return {
                label: fullName || email,
                value: id,
                valueLabel: fullName || email,
                primaryLabel: role as string,
            };
        })
        .sortBy((member) => member.label.toLowerCase())
        .toJS();
}

/**
 *
 * @param {*} member - Immutable map corresponding member object from backend.
 * @returns computed member properties as a plain JS object.
 */
export function getMemberDetails(member: ImmutableMap<User>) {
    const fullName = member.get('last_name')
        ? `${member.get('first_name')} ${member.get('last_name')}`
        : member.get('first_name');
    const id = member.get('id');
    const email = member.get('email');
    const role = member.get('role');
    const photoUrl = member.get('photo_url');
    return {
        id,
        fullName,
        email,
        role,
        photoUrl,
    };
}
